import type { VacationSettings } from '@/api';
import { localZone } from '@/features/calendar/zone';

/**
 * Out of office: the day arithmetic both the settings page and the mail-view
 * banner need, in one place so they cannot disagree about whether it is on.
 *
 * Every day here is a `YYYY-MM-DD` string in the zone the reply was saved
 * with. That is also how the mail server compares them (Sieve `currentdate
 * :value "ge" "date"`), and fixed-width ISO days order correctly as plain
 * strings, so no `Date` arithmetic — and no DST edge — is involved.
 */

/** The repeat windows offered. Gmail sends once per sender every 4 days. */
export const VACATION_DAY_CHOICES: readonly number[] = [1, 2, 3, 4, 7, 14];
export const DEFAULT_VACATION_DAYS = 4;

/** The zone the owner's dates mean: Settings → General, else the device's. */
export function vacationZone(preferenceZone: string): string {
  return preferenceZone === '' ? localZone() : preferenceZone;
}

/** Today in `zone`, as `YYYY-MM-DD`. `now` is injectable for tests. */
export function todayIn(zone: string, now: Date = new Date()): string {
  try {
    // en-CA writes YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    // An unknown zone name: the device's day is a better answer than a throw.
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  }
}

/** "Mon, Sep 29" — a calendar day, rendered without shifting it through a zone. */
export function formatVacationDay(day: string, locale?: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (match === null) return day;
  const at = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat(locale === '' ? undefined : locale, {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(at);
}

export type VacationPhase = 'off' | 'scheduled' | 'active' | 'ended';

/**
 * Where a saved reply stands today, in its own zone.
 *
 * `ended` is still `enabled` on the server — the date test simply stops
 * matching — which is why the banner has to ask this rather than read the
 * switch.
 */
export function vacationPhase(
  vacation: VacationSettings | null | undefined,
  now: Date = new Date(),
): VacationPhase {
  if (!vacation?.enabled) return 'off';
  const today = todayIn(vacation.timeZone || localZone(), now);
  if (vacation.endDate !== '' && today > vacation.endDate) return 'ended';
  if (vacation.startDate !== '' && today < vacation.startDate) return 'scheduled';
  return 'active';
}

/** A fresh card: off, starting today, Gmail's four-day window. */
export function blankVacation(zone: string, now: Date = new Date()): VacationSettings {
  return {
    enabled: false,
    startDate: todayIn(zone, now),
    endDate: '',
    timeZone: zone,
    subject: '',
    body: '',
    days: DEFAULT_VACATION_DAYS,
  };
}
